import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSection,
  TableKit,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import {
  financeStructureApi,
  formatAgorot,
  shekelsToAgorot,
  type CampaignMapping,
  type FinanceCategory,
} from "../../api/financeStructureApi";
import { toClientApiError } from "../../api/httpClient";
import { ListPageHeader } from "../../ui/ListPageHeader";

type Props = { writeCustomerId: string };

export function CampaignsMappingsTab({ writeCustomerId }: Props) {
  const [rows, setRows] = useState<CampaignMapping[]>([]);
  const [categories, setCategories] = useState<FinanceCategory[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [mappings, cats] = await Promise.all([
        financeStructureApi.listCampaignMappings(),
        financeStructureApi.listCategories(),
      ]);
      setRows(mappings);
      setCategories(cats.filter((c) => !c.isSystem));
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        key: "campaignName",
        title: "קמפיין",
        sortable: true,
        filterable: true,
        minWidth: 160,
      },
      {
        key: "targetAmount",
        title: "יעד",
        render: (_value: unknown, row: Record<string, unknown>) =>
          formatAgorot(Number(row.targetAmount ?? 0)),
      },
      {
        key: "currentAmount",
        title: "נוכחי",
        render: (_value: unknown, row: Record<string, unknown>) =>
          formatAgorot(Number(row.currentAmount ?? 0)),
      },
      {
        key: "categoryId",
        title: "קטגוריה",
        minWidth: 200,
        render: (_value: unknown, row: Record<string, unknown>) => {
          const r = row as unknown as CampaignMapping;
          const current = r.categoryId ?? "";
          return (
            <FormField
              type="select"
              value={current}
              onChange={(next) => {
                void financeStructureApi
                  .setCampaignMapping(r.customerId, r.campaignId, next || null)
                  .then(load)
                  .catch((err) => setError(toClientApiError(err).message));
              }}
              options={[
                { value: "", label: "— ללא מיפוי —" },
                ...categories.map((c) => ({
                  value: c.id,
                  label: c.displayName ?? c.name,
                })),
              ]}
            />
          );
        },
      },
      {
        key: "isActive",
        title: "סטטוס",
        render: (_value: unknown, row: Record<string, unknown>) =>
          row.isActive ? "פעיל" : "לא פעיל",
      },
    ],
    [categories, load],
  );

  return (
    <div>
      <ListPageHeader
        onRefresh={() => void load()}
        primaryLabel="קמפיין חדש"
        onPrimary={() => {
          setName("");
          setTarget("");
          setCategoryId("");
          setCreateOpen(true);
        }}
      />
      {error ? <p className="form-error">{error}</p> : null}
      {loading ? <p>טוען…</p> : null}
      {!loading ? (
        <TableKit
          columns={columns}
          rows={rows as unknown as Record<string, unknown>[]}
          rowKey="campaignId"
          emptyMessage="אין קמפיינים — צרו קמפיין חדש"
        />
      ) : null}

      <FormDialog open={createOpen} onClose={() => setCreateOpen(false)} size="md">
        <FormCard
          title="קמפיין חדש"
          onSave={() => {
            void (async () => {
              setSaving(true);
              try {
                await financeStructureApi.createCampaign(writeCustomerId, {
                  name: name.trim(),
                  targetAmountAgorot: target
                    ? shekelsToAgorot(Number(target))
                    : 0,
                  categoryId: categoryId || null,
                });
                setCreateOpen(false);
                await load();
              } catch (err) {
                setError(toClientApiError(err).message);
              } finally {
                setSaving(false);
              }
            })();
          }}
          onCancel={() => setCreateOpen(false)}
          saveLabel="יצירה"
          cancelLabel="ביטול"
          saving={saving}
          saveDisabled={saving || !name.trim()}
        >
          <FormSection title="פרטים">
            <FormGrid>
              <FormField label="שם" required value={name} onChange={setName} />
              <FormField
                label="יעד (₪)"
                type="number"
                value={target}
                onChange={setTarget}
                inputProps={{ min: 0, step: "0.01" }}
              />
              <FormField
                label="קטגוריה"
                value={categoryId}
                onChange={setCategoryId}
                options={[
                  { value: "", label: "— ללא —" },
                  ...categories.map((c) => ({
                    value: c.id,
                    label: c.displayName ?? c.name,
                  })),
                ]}
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>
    </div>
  );
}
